import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { type RouteComponentProps, useLocation } from "react-router";
import { TierPackageTypes, type UserModel } from "@doitintl/cmp-models";
import { Button, Container, Stack, Typography } from "@mui/material";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

import { useAvaDialogContext } from "../../Components/Ava2/AvaDialogContextProvider";
import Hide from "../../Components/HideChildren/Hide";
import { useAuthContext } from "../../Context/AuthContext";
import { useCustomerContext } from "../../Context/CustomerContext";
import { useTier } from "../../Context/TierProvider";
import { useUserContext } from "../../Context/UserContext";
import TierTrialEndAlert from "../../Navigation/Components/HeaderAlerts/TrialEnd/TierTrialEndAlert";
import { consoleErrorWithSentry } from "../../utils";
import { useFullScreen } from "../../utils/dialog";
import { getCustomerTierTrialDates } from "../../utils/tiers";
import { downloadTickets, sendNewDefaultEmailCC } from "../Actions/TicketAction";
import DefaultEmailCCDialog from "../Components/DefaultEmailCCDialog";
import { PlatformFilters } from "../Components/PlatformFilters";
import { SettingsButton } from "../Components/SettingsButton";
import SupportTicketsTable from "../Components/TicketsFilterTable/SupportTicketsTable";
import { ticketsCsvFileName } from "../consts";
import { useZendeskTicketsContext } from "../Context/ZendeskTicketsContext";
import { useFilterCloudFirestore } from "../hooks/useFilterCloudFirestore";
import { type Organization } from "../types";

type Params = {
  customerId: string;
};

type FunctionProps = {
  downloadTickets: () => void;
  toggleOrganizationSharing: (org: Organization) => void;
  sendNewDefaultEmailCC: (data: string[]) => void;
};

type StateProps = {
  organization: Organization;
  isDownloadingCSV: boolean;
  defaultEmailsList: string[];
  customerUsers: UserModel[];
};

type Props = RouteComponentProps<Params> & FunctionProps & StateProps;

const TicketsContainer = ({
  isDownloadingCSV,
  defaultEmailsList,
  customerUsers,
  downloadTickets,
  sendNewDefaultEmailCC,
}: Props) => {
  const { isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const { customerOrPresentationModeCustomer: customer } = useCustomerContext();
  const { userRoles } = useUserContext({ requiredRoles: true });
  const { isActiveTrialCustomer } = useTier();
  const { zendeskTickets, error: ticketsError, organization, toggleOrganizationSharing } = useZendeskTicketsContext();
  const { openCaseIQWithEmptyState, isAvaDialogOpen, isAvaDialogMinimized } = useAvaDialogContext();

  const { search } = useLocation();

  const shouldOpenCaseIQ = search.includes("open-case-iq=true");

  const { t } = useTranslation("services");
  const { isMobile } = useFullScreen("sm");

  const [isDefaultEmailsDialogOpen, setIsDefaultEmailsDialogOpen] = useState(false);

  useEffect(() => {
    if (shouldOpenCaseIQ && (!isAvaDialogOpen || isAvaDialogMinimized)) {
      openCaseIQWithEmptyState();
    }
  }, [shouldOpenCaseIQ, openCaseIQWithEmptyState, isAvaDialogOpen, isAvaDialogMinimized]);

  useEffect(() => {
    if (ticketsError) {
      consoleErrorWithSentry(ticketsError);
    }
  }, [ticketsError]);

  const isCurrentUserUserManager = useMemo(() => !!userRoles.usersManager || !!userRoles.doitEmployee, [userRoles]);

  const { filteredTicketsFirestore, onClickSupportFilter, selectedPlatformFilter, supportPlatformsAvailable } =
    useFilterCloudFirestore(zendeskTickets);

  const { start, end } = getCustomerTierTrialDates(customer, TierPackageTypes.SOLVE);

  return (
    <>
      <Container maxWidth="xl" disableGutters sx={{ px: 1 }}>
        {isActiveTrialCustomer(TierPackageTypes.SOLVE) && end && (
          <TierTrialEndAlert solveTrialStartDate={start} solveTrialEndDate={end} />
        )}
        <Snackbar
          open={isDownloadingCSV}
          autoHideDuration={6000}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert icon={false} elevation={6} variant="filled" severity="info">
            Downloading {ticketsCsvFileName} ...
          </Alert>
        </Snackbar>

        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={2}
          sx={{
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            pt: 5,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "500",
            }}
          >
            {t("TicketsContainer.title")}
          </Typography>

          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: isMobile ? "space-between" : "flex-end",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Button variant="contained" color="primary" onClick={openCaseIQWithEmptyState}>
              Create a request
            </Button>

            <PlatformFilters
              onClickFilter={onClickSupportFilter}
              selectedFilter={selectedPlatformFilter}
              platformFilters={supportPlatformsAvailable}
            />

            {userRoles.billingProfilesAdmin && (
              <Hide smDown>
                <SettingsButton
                  toggleOrganizationSharing={toggleOrganizationSharing}
                  organization={organization}
                  downloadTickets={downloadTickets}
                  setIsDefaultEmailsDialogOpen={setIsDefaultEmailsDialogOpen}
                  isCurrentUserUserManager={!!isCurrentUserUserManager}
                />
              </Hide>
            )}
          </Stack>
        </Stack>

        <SupportTicketsTable zendeskTickets={filteredTicketsFirestore} isDoitEmployee={isDoitEmployee} />

        <DefaultEmailCCDialog
          users={customerUsers}
          open={isDefaultEmailsDialogOpen}
          onClose={() => {
            setIsDefaultEmailsDialogOpen(false);
          }}
          defaultEmailsListFromDB={defaultEmailsList}
          sendNewDefaultEmailCC={sendNewDefaultEmailCC}
        />
      </Container>
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  downloadTickets: () => dispatch(downloadTickets()),
  sendNewDefaultEmailCC: (data) => dispatch(sendNewDefaultEmailCC(data)),
});

type TicketsStateProps = {
  tickets: StateProps;
};

const mapStateToProps = (state: TicketsStateProps) => ({
  isDownloadingCSV: state.tickets.isDownloadingCSV,
  defaultEmailsList: state.tickets.defaultEmailsList,
  customerUsers: state.tickets.customerUsers,
});

export default connect(mapStateToProps, mapDispatchToProps)(TicketsContainer);
